import React, { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../lib/AuthContext';
import {
  Bell, CheckCheck, Info, AlertTriangle, CheckCircle2,
  MessageCircle, Calendar, Briefcase, Megaphone
} from 'lucide-react';
import { format } from 'date-fns';

interface Notification {
  id: string;
  title: string;
  body: string;
  type: string;
  read: boolean;
  created_at: string;
  data?: any;
}

const typeIcons: Record<string, { icon: React.ReactNode; color: string; bg: string }> = {
  info: { icon: <Info size={16} />, color: 'text-blue-600', bg: 'bg-blue-50' },
  success: { icon: <CheckCircle2 size={16} />, color: 'text-emerald-600', bg: 'bg-emerald-50' },
  warning: { icon: <AlertTriangle size={16} />, color: 'text-amber-600', bg: 'bg-amber-50' },
  error: { icon: <AlertTriangle size={16} />, color: 'text-red-600', bg: 'bg-red-50' },
  interview_update: { icon: <Calendar size={16} />, color: 'text-violet-600', bg: 'bg-violet-50' },
  shortlist: { icon: <CheckCircle2 size={16} />, color: 'text-emerald-600', bg: 'bg-emerald-50' },
  general: { icon: <Bell size={16} />, color: 'text-slate-600', bg: 'bg-slate-100' },
  reminder: { icon: <Bell size={16} />, color: 'text-amber-600', bg: 'bg-amber-50' },
  campaign_message: { icon: <Megaphone size={16} />, color: 'text-blue-600', bg: 'bg-blue-50' },
};

export default function Notifications() {
  const { activeUserId } = useAuth();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'unread'>('all');

  useEffect(() => {
    if (activeUserId) fetchNotifications();
  }, [activeUserId]);

  // Real-time subscription
  useEffect(() => {
    if (!activeUserId) return;
    const channel = supabase
      .channel('notif-realtime')
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'notifications',
        filter: `user_id=eq.${activeUserId}`,
      }, (payload) => {
        setNotifications(prev => [payload.new as Notification, ...prev]);
      })
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [activeUserId]);

  const fetchNotifications = async () => {
    try {
      const { data } = await supabase
        .from('notifications')
        .select('*')
        .eq('user_id', activeUserId!)
        .order('created_at', { ascending: false })
        .limit(100);
      setNotifications(data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const markAllRead = async () => {
    await supabase
      .from('notifications')
      .update({ read: true })
      .eq('user_id', activeUserId!)
      .eq('read', false);
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
  };

  const markRead = async (id: string) => {
    await supabase.from('notifications').update({ read: true }).eq('id', id);
    setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
  };

  const filtered = filter === 'unread' ? notifications.filter(n => !n.read) : notifications;
  const unreadCount = notifications.filter(n => !n.read).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-[3px] border-slate-200 border-t-slate-800 rounded-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-in fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-black text-slate-900 tracking-tight" style={{ fontFamily: "'Inter', system-ui" }}>Notifications</h1>
          <p className="text-slate-500 mt-1 text-[15px]">{unreadCount} unread</p>
        </div>
        {unreadCount > 0 && (
          <button
            onClick={markAllRead}
            className="flex items-center gap-2 text-[13px] font-semibold text-blue-600 hover:text-blue-700 bg-blue-50 hover:bg-blue-100 px-4 py-2 rounded-xl transition-colors"
          >
            <CheckCheck size={15} />
            Mark all read
          </button>
        )}
      </div>

      {/* Filter pills */}
      <div className="flex gap-2">
        {(['all', 'unread'] as const).map(f => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`px-4 py-2 rounded-xl text-[13px] font-semibold transition-colors ${filter === f
                ? 'bg-slate-900 text-white'
                : 'bg-white text-slate-500 border border-slate-200 hover:bg-slate-50'
              }`}
          >
            {f === 'all' ? 'All' : `Unread (${unreadCount})`}
          </button>
        ))}
      </div>

      {/* Notification list */}
      {filtered.length === 0 ? (
        <div className="bg-white rounded-2xl border border-slate-200/60 p-12 text-center shadow-sm">
          <Bell size={40} className="mx-auto text-slate-200 mb-3" />
          <p className="text-slate-400 text-[14px]">{filter === 'unread' ? 'No unread notifications' : 'No notifications yet'}</p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-slate-200/60 overflow-hidden shadow-sm">
          {filtered.map((notif, i) => {
            const cfg = typeIcons[notif.type] || typeIcons.general;
            return (
              <div
                key={notif.id}
                onClick={() => !notif.read && markRead(notif.id)}
                className={`flex items-start gap-4 px-5 py-4 cursor-pointer transition-colors ${i > 0 ? 'border-t border-slate-100' : ''
                  } ${!notif.read ? 'bg-blue-50/30 hover:bg-blue-50/50' : 'hover:bg-slate-50/50'}`}
              >
                <div className={`w-9 h-9 rounded-xl ${cfg.bg} flex items-center justify-center ${cfg.color} shrink-0 mt-0.5`}>
                  {cfg.icon}
                </div>
                <div className="flex-1 min-w-0">
                  <p className={`text-[13px] font-semibold truncate ${!notif.read ? 'text-slate-900' : 'text-slate-600'}`}>
                    {notif.title}
                  </p>
                  {notif.body && (
                    <p className="text-[12px] text-slate-400 mt-0.5 line-clamp-2">{notif.body}</p>
                  )}
                  <p className="text-[10px] text-slate-400 mt-1.5">{format(new Date(notif.created_at), 'MMM d · h:mm a')}</p>
                </div>
                {!notif.read && (
                  <div className="w-2 h-2 bg-blue-500 rounded-full shrink-0 mt-2" />
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
